import { ClientSearch } from "@core/components/client-search";
import { useOutsideClick } from "@core/hooks/use-outside-click";
import { useCreateEvent, useUpdateEvent } from "creso-core/hooks";
import { NoteListItem, NoteTask } from "creso-core/types";
import { formatDate } from "date-fns";
import { CheckIcon, X } from "lucide-react";
import { RefObject, useEffect, useRef, useState } from "react";
import { Button, cn, DatePicker, Textarea } from "web-design-system";
import { handleError } from "@/utils/error";
import { NotebookFilters, useNotebookContext } from "./notebook-context";

const defaultTaskGenerator = (
  filters: NotebookFilters,
  note?: NoteListItem | null
): NoteTask => {
  const { client, date } = filters;
  return {
    note: "",
    date: formatDate(date ?? new Date(), "yyyy-MM-dd"),
    type: "NOTE",
    client_id: client?.id,
    client_name: client?.name ?? "",
    is_deprecated: false,
    id: -1,
    note_id: note?.id,
  };
};

export function TaskEditor({
  onFinish,
  task,
  className,
}: {
  onFinish: () => void;
  task?: NoteTask;
  className?: string;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useOutsideClick(containerRef as RefObject<HTMLDivElement>, onFinish);

  const { filters, selectedNote, clientIds } = useNotebookContext();

  const [localTask, setLocalTask] = useState<NoteTask>(
    task ?? defaultTaskGenerator(filters, selectedNote)
  );

  useEffect(
    () => setLocalTask(task ?? defaultTaskGenerator(filters, selectedNote)),
    [task, filters, selectedNote]
  );

  function resizeTextarea() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }

  function handleChange() {
    const el = textareaRef.current;
    if (!el) return;
    setLocalTask((t) => ({ ...t, note: el.value }));
    resizeTextarea();
  }

  useEffect(() => {
    resizeTextarea();
  }, []);

  const { mutateAsync: updateTask } = useUpdateEvent();

  const { mutateAsync: createTask } = useCreateEvent();

  const handleSave = async () => {
    try {
      if (task?.id && task.id !== -1) {
        await updateTask({
          ...localTask,
          client_id: localTask.client_id ?? selectedNote?.client_id,
          date: localTask.date || null,
          id: task.id,
          type: "NOTE",
        });
      } else {
        await createTask({
          note: localTask.note,
          client_id: localTask.client_id,
          note_id: localTask?.note_id,
          date: localTask.date || null,
          type: "NOTE",
        });
      }
      onFinish();
    } catch (e) {
      handleError(e, "Failed to save task.");
    }
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "border border-border flex-1 rounded-lg p-3 -my-2 gap-6 flex flex-col",
        className
      )}
    >
      <Textarea
        ref={textareaRef}
        rows={1}
        autoFocus
        value={localTask.note}
        className="min-h-0 p-0 border-0 text-sm ring-0! rounded-none outline-0! resize-none overflow-hidden"
        onChange={handleChange}
        placeholder="Type your task..."
      />
      <div className="flex flex-row gap-3">
        <div className="w-36">
          <DatePicker
            options={{ modal: true, portal: false, side: "top" }}
            value={localTask.date}
            onChange={(d) => setLocalTask((t) => ({ ...t, date: d }))}
          />
        </div>
        <div className="flex-1 w-45 mr-5">
          {localTask?.note_id ? null : (
            <ClientSearch
              client={
                localTask.client_id
                  ? { id: localTask.client_id, name: localTask.client_name }
                  : null
              }
              clientIds={clientIds}
              placeholder={clientIds ? "Select member" : "Select client"}
              onSelect={(c) =>
                setLocalTask((t) => ({
                  ...t,
                  client_id: c?.id,
                  client_name: c?.name as string,
                }))
              }
              options={{ portal: false }}
            />
          )}
        </div>
        <Button
          onClick={() => {
            setLocalTask(task ?? defaultTaskGenerator(filters));
            onFinish();
          }}
          className="shrink-0 ml-auto"
          size="icon"
          variant="outline"
        >
          <X />
        </Button>
        <Button onClick={handleSave} className="shrink-0" size="icon">
          <CheckIcon />
        </Button>
      </div>
    </div>
  );
}
